import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  DescriptionOutlined as DocumentIcon,
  EditOutlined as EditIcon,
  NotesOutlined as NoteIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { libraryApi } from '../../services/api';
import { Library, LibraryEntry } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatBytes, formatDate } from '../../utils/format';

interface Props {
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
}

// What the API can read the text of.
const ACCEPT = '.pdf,.docx,.txt';
const MAX_BYTES = 5 * 1024 * 1024;
const NOT_ONE_JOB = '';

interface Draft {
  /** The entry being changed; null when adding. */
  entry: LibraryEntry | null;
  kind: 'note' | 'document';
  title: string;
  employer: string;
  text: string;
  file: File | null;
}

const EMPTY: Draft = { entry: null, kind: 'note', title: '', employer: NOT_ONE_JOB, text: '', file: null };

/**
 * The Experience library: what a person has done beyond what their resume
 * says, written down or added as documents. A tailored resume draws on it as
 * it draws on the resume. The page links here as `#library`.
 */
const ExperienceLibrary: React.FC<Props> = ({ notify }) => {
  const [library, setLibrary] = useState<Library | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LibraryEntry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setLibrary(await libraryApi.list());
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load your Experience library.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // The match on a job links here; the card is drawn after the page, so go to it once it is.
  useEffect(() => {
    if (library && window.location.hash === '#library') document.getElementById('library')?.scrollIntoView();
  }, [library]);

  const open = (next: Draft) => {
    setProblem(null);
    setSaving(false);
    setDraft(next);
  };

  const edit = async (entry: LibraryEntry) => {
    try {
      const full = await libraryApi.get(entry.id);
      open({ entry: full, kind: full.kind, title: full.title, employer: full.employer, text: full.text ?? '', file: null });
    } catch (e) {
      notify(errorMessage(e, 'Could not open that entry.'), 'error');
    }
  };

  const chooseFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!draft || !file) return;
    if (file.size > MAX_BYTES) {
      setProblem(`That file is ${formatBytes(file.size)}; the limit is ${formatBytes(MAX_BYTES)}.`);
      return;
    }
    setProblem(null);
    setDraft({ ...draft, file });
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setProblem(null);
    try {
      if (draft.entry) {
        await libraryApi.update(draft.entry.id, { title: draft.title, employer: draft.employer, ...(draft.kind === 'note' ? { text: draft.text } : {}) });
        notify('Saved. Resumes tailored from now on draw on it.');
      } else if (draft.kind === 'note') {
        await libraryApi.addNote({ text: draft.text, title: draft.title, employer: draft.employer });
        notify('Added to your Experience library.');
      } else if (draft.file) {
        await libraryApi.addDocument(draft.file, { title: draft.title, employer: draft.employer });
        notify('Document added to your Experience library.');
      }
      setDraft(null);
      await load();
    } catch (e) {
      setProblem(errorMessage(e, 'It could not be saved.'));
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await libraryApi.remove(pendingDelete.id);
      setLibrary((current) => (current ? { ...current, entries: current.entries.filter((entry) => entry.id !== pendingDelete.id) } : current));
      notify('Removed from your Experience library.');
      setPendingDelete(null);
    } catch (e) {
      notify(errorMessage(e, 'It could not be removed.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const entries = library?.entries ?? [];
  const jobs = library?.jobs ?? [];
  const full = library !== null && entries.length >= library.limit;
  const ready = draft ? (draft.entry ? draft.kind === 'document' || draft.text.trim().length >= 3 : draft.kind === 'note' ? draft.text.trim().length >= 3 : draft.file !== null) : false;

  return (
    <Card variant="outlined" component="section" id="library" aria-labelledby="library-title" sx={{ scrollMarginTop: '88px' }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
          <Box>
            <Typography id="library-title" variant="h6" fontWeight={600}>
              Experience library
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
              Everything you have done that your resume has no room for: projects, results, tools you used, things a review
              said about your work. When a resume is tailored for a job, JobFlo draws on this as well as on your resume, so
              the experience that job asks for can appear even if your resume leaves it out.
            </Typography>
          </Box>
          <Tooltip title={full ? `The library holds up to ${library?.limit} entries. Remove one to add another.` : ''}>
            <span>
              <Button variant="outlined" startIcon={<AddIcon />} disabled={full || library === null} onClick={() => open(EMPTY)} sx={{ whiteSpace: 'nowrap' }}>
                Add to library
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {library && entries.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nothing here yet. Start with what your resume leaves out: write a few lines about a job, or add a document such
            as an old resume, a project write-up or a performance review.
          </Typography>
        )}
        <Stack spacing={1}>
          {entries.map((entry) => (
            <Box key={entry.id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ color: 'text.secondary', pt: 0.25 }} aria-hidden="true">
                {entry.kind === 'document' ? <DocumentIcon fontSize="small" /> : <NoteIcon fontSize="small" />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {entry.title}
                  </Typography>
                  <Chip size="small" variant="outlined" label={entry.employer || (entry.kind === 'document' ? 'Job as the document says' : 'Not about one job')} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {entry.excerpt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {entry.kind === 'document' ? `Document${entry.filename ? `: ${entry.filename}` : ''}` : 'Written by you'}
                  {entry.lines !== null ? ` · ${entry.lines} ${entry.lines === 1 ? 'line' : 'lines'}` : ' · read when your next resume is tailored'}
                  {` · added ${formatDate(entry.created_at)}`}
                </Typography>
              </Box>
              <Box sx={{ whiteSpace: 'nowrap' }}>
                <Tooltip title={entry.kind === 'document' ? 'View, rename or change the job it is about' : 'Edit'}>
                  <IconButton size="small" aria-label={`Edit ${entry.title}`} onClick={() => edit(entry)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove">
                  <IconButton size="small" aria-label={`Remove ${entry.title}`} onClick={() => setPendingDelete(entry)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Stack>
      </CardContent>

      <Dialog open={draft !== null} onClose={saving ? undefined : () => setDraft(null)} fullWidth maxWidth="sm" aria-labelledby="library-add-title">
        <DialogTitle id="library-add-title">{draft?.entry ? `Edit "${draft.entry.title}"` : 'Add to your Experience library'}</DialogTitle>
        <DialogContent dividers>
          {draft && (
            <Stack spacing={2}>
              {!draft.entry && (
                <Tabs value={draft.kind} onChange={(_, kind: 'note' | 'document') => open({ ...draft, kind })} aria-label="How to add it">
                  <Tab value="note" label="Write it" />
                  <Tab value="document" label="Add a document" />
                </Tabs>
              )}
              <Alert severity="warning">
                Add only what is yours to share. Performance reviews, internal documents and client work can hold your
                employer&apos;s confidential information: leave those details out. What you add is kept in your JobFlo account,
                and its text is sent to the model that tailors your resumes.
              </Alert>
              {problem && <Alert severity="error">{problem}</Alert>}
              {draft.kind === 'note' ? (
                <TextField
                  label="What you did"
                  value={draft.text}
                  onChange={(e) => setDraft({ ...draft, text: e.target.value })}
                  multiline
                  minRows={5}
                  fullWidth
                  disabled={saving}
                  placeholder={'Ran the ledger service on Kubernetes across 3 clusters\nIntroduced Terraform for all infrastructure changes'}
                  helperText="One thing per line, the way you would say it on a resume: what you did, with what, and the result. Each line can become a line of a tailored resume."
                />
              ) : draft.entry ? (
                <TextField label="What the document says" value={draft.text} multiline minRows={5} maxRows={12} fullWidth InputProps={{ readOnly: true }} helperText="A document's text cannot be changed here. Remove it and add the corrected one." />
              ) : (
                <Box>
                  <input ref={fileInput} type="file" hidden accept={ACCEPT} onChange={chooseFile} />
                  <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInput.current?.click()} disabled={saving}>
                    {draft.file ? 'Choose another file' : 'Choose a file'}
                  </Button>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {draft.file ? `${draft.file.name} (${formatBytes(draft.file.size)})` : 'An old resume, a project write-up, a performance review, a portfolio page saved as PDF.'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, Word (.docx) or plain text, up to {formatBytes(MAX_BYTES)}. JobFlo keeps the text and not the file, and
                    takes from it only what it says you did.
                  </Typography>
                </Box>
              )}
              <TextField
                select
                label="Which job is it about?"
                value={jobs.some((job) => job.employer === draft.employer) || draft.employer === NOT_ONE_JOB ? draft.employer : NOT_ONE_JOB}
                onChange={(e) => setDraft({ ...draft, employer: e.target.value })}
                fullWidth
                disabled={saving}
                helperText={
                  draft.kind === 'document'
                    ? 'Choose a job if the whole document is about it. Otherwise leave this, and JobFlo goes by what the document says.'
                    : 'Lines about a job can join that job on a tailored resume. Lines about no one job can still supply skills and the summary.'
                }
              >
                <MenuItem value={NOT_ONE_JOB}>{draft.kind === 'document' ? 'Several jobs, or none' : 'Not about one job'}</MenuItem>
                {jobs.map((job) => (
                  <MenuItem key={job.employer} value={job.employer}>
                    {[job.title, job.employer].filter(Boolean).join(', ')}
                  </MenuItem>
                ))}
              </TextField>
              {jobs.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  Your jobs appear here once a resume has been tailored, or when they are on your Profile under Experience.
                </Typography>
              )}
              <TextField
                label="Name"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                fullWidth
                disabled={saving}
                inputProps={{ maxLength: 160 }}
                helperText="Optional. A short name so you can find it again."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraft(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !ready}>
            {saving ? 'Saving...' : draft?.entry ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingDelete !== null} onClose={deleting ? undefined : () => setPendingDelete(null)} maxWidth="xs" aria-labelledby="library-delete-title">
        <DialogTitle id="library-delete-title">Remove this from your library?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            &quot;{pendingDelete?.title}&quot; is deleted. Resumes already tailored keep the lines they took from it; new ones no
            longer draw on it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={remove} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ExperienceLibrary;
